import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Div, FormItem, Headline, Input, Spinner, Text } from '@vkontakte/vkui';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { useWeddingStore } from '../stores/wedding';
import { copyText } from '../api/share';
import { buildVkAppLink } from '../utils/joinCode';
import { buildVKIdAuthorizeUrl, takeVKIdCallback, clearOAuthFromUrl, vkIdLogin } from '../api/vkOAuth';
import type { AuthResponse, WeddingPreview } from '../types';

interface Props {
  code: string;
  onResolved: (result: 'joined' | 'opened' | 'dismissed') => void;
}

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function JoinGate({ code, onResolved }: Props) {
  const { isAuthenticated, setSession } = useAuthStore();
  const [preview, setPreview] = useState<WeddingPreview | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState('');

  const load = useCallback(async () => {
    setPreview(null);
    setFailed(false);
    setError(null);
    try {
      const p = await api.get<WeddingPreview>(`weddings/preview?code=${encodeURIComponent(code)}`);
      setPreview(p);
    } catch {
      setFailed(true);
    }
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api.get<WeddingPreview>(`weddings/preview?code=${encodeURIComponent(code)}`);
        if (!cancelled) setPreview(p);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Возврат с VK ID OAuth (вход прямо на лендинге) → сразу присоединяемся
  useEffect(() => {
    const cb = takeVKIdCallback();
    if (!cb) return;
    clearOAuthFromUrl();
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const res = await vkIdLogin(cb.code, cb.device_id);
        setSession(res as AuthResponse);
        const target = localStorage.getItem('colibri.pending_join_code') || code;
        localStorage.removeItem('colibri.pending_join_code');
        await useWeddingStore.getState().joinWedding(target);
        if (!cancelled) setJoined(true);
      } catch {
        if (!cancelled) {
          setError('Не удалось завершить вход. Попробуйте ещё раз.');
          setBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, setSession]);

  const joinOnLanding = useCallback(async (target: string = code) => {
    const value = target.trim().toUpperCase();
    if (!value) {
      setError('Введите код приглашения');
      return;
    }
    setBusy(true);
    setError(null);
    if (!localStorage.getItem('colibri_token')) {
      localStorage.setItem('colibri.pending_join_code', value);
      localStorage.setItem('colibri.pending_join_auto', '1');
      try {
        window.location.assign(await buildVKIdAuthorizeUrl());
      } catch {
        setError('Не удалось открыть вход ВКонтакте');
        setBusy(false);
      }
      return;
    }
    try {
      await useWeddingStore.getState().joinWedding(value);
      setJoined(true);
    } catch {
      setError('Не удалось присоединиться. Попробуйте ещё раз.');
      setBusy(false);
    }
  }, [code]);

  // После VK ID входа вернулись сюда с кодом из localStorage — пользователь уже
  // нажал «Присоединиться» до ухода на вход, завершаем без лишнего тапа.
  const autoTriedRef = useRef(false);
  useEffect(() => {
    if (autoTriedRef.current || !isAuthenticated) return;
    if (localStorage.getItem('colibri.pending_join_auto') === '1') {
      autoTriedRef.current = true;
      localStorage.removeItem('colibri.pending_join_auto');
      void joinOnLanding(code);
    }
  }, [isAuthenticated, code, joinOnLanding]);

  async function copyCode(value: string) {
    const ok = await copyText(value);
    setSnack(ok ? 'Код скопирован' : 'Не удалось скопировать');
  }

  function openVkApp(link: string) {
    // Встроенный браузер ВК часто молча игнорирует intent:// — кидаем обе стратегии
    try {
      const a = document.createElement('a');
      a.href = link;
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      /* ignore */
    }
    try {
      window.location.assign(link);
    } catch {
      /* ignore */
    }
  }

  async function openInVk(value: string) {
    await copyText(value);
    openVkApp(buildVkAppLink(value));
  }

  async function doJoin(joinCode: string) {
    const value = joinCode.trim().toUpperCase();
    if (!value) {
      setError('Введите код приглашения');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await useWeddingStore.getState().joinWedding(value);
      onResolved('joined');
    } catch {
      setError('Не удалось присоединиться. Проверьте код — возможно, ссылка устарела.');
      setBusy(false);
    }
  }

  async function doOpen(weddingId: string) {
    setBusy(true);
    try {
      await useWeddingStore.getState().selectWedding(weddingId);
      onResolved('opened');
    } catch {
      setError('Не удалось открыть свадьбу');
      setBusy(false);
    }
  }

  const name = preview?.name ? `«${preview.name}»` : '';
  const date = formatDate(preview?.wedding_date);

  const codeBlock = (
    <div style={{ textAlign: 'center', margin: '14px 0' }}>
      <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>Код приглашения</Text>
      <Headline level="1" style={{ fontSize: 30, letterSpacing: 3, margin: '4px 0 10px' }}>{code}</Headline>
      <Button size="m" mode="secondary" disabled={busy} onClick={() => void copyCode(code)}>
        Скопировать код
      </Button>
    </div>
  );

  let body: React.ReactNode;

  if (failed) {
    body = (
      <>
        <Headline level="1">Не удалось загрузить приглашение</Headline>
        <Text style={{ marginTop: 10, marginBottom: 16 }}>
          Проверьте соединение и попробуйте ещё раз. Код приглашения: {code}
        </Text>
        <Button size="l" stretched mode="primary" disabled={busy} onClick={() => void load()}>
          Повторить
        </Button>
        <Button size="l" stretched mode="secondary" style={{ marginTop: 8 }} disabled={busy} onClick={() => onResolved('dismissed')}>
          Закрыть
        </Button>
      </>
    );
  } else if (!preview) {
    body = (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spinner size="l" />
      </div>
    );
  } else if (joined) {
    body = (
      <>
        <Headline level="1">Вы присоединились!</Headline>
        <Text style={{ marginTop: 10, marginBottom: 6, fontSize: 18, fontWeight: 600 }}>{name}</Text>
        <Text style={{ marginBottom: 16, color: 'var(--vkui--color_text_secondary)' }}>
          Свадьба уже в вашем аккаунте. Откройте приложение Colibri в ВКонтакте — она появится там.
        </Text>
        <Button size="l" stretched mode="primary" onClick={() => openVkApp(buildVkAppLink(code))}>
          Открыть Colibri в ВКонтакте
        </Button>
        <Text style={{ marginTop: 10, marginBottom: 8, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
          Если не открылось само: закройте эту страницу и откройте Colibri в ВКонтакте — свадьба уже там.
        </Text>
        <Button size="l" stretched mode="secondary" onClick={() => onResolved('joined')}>
          Готово
        </Button>
      </>
    );
  } else if (preview.status === 'not_found') {
    body = (
      <>
        <Headline level="1">Ссылка не работает</Headline>
        <Text style={{ marginTop: 10, marginBottom: 16 }}>
          Свадьба не найдена — возможно, ссылка устарела. Попросите у организатора новую или введите код вручную.
        </Text>
        <FormItem top="Код приглашения">
          <Input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            placeholder="Например: 0N2K6HNU"
            autoCapitalize="characters"
          />
        </FormItem>
        {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
        <Button size="l" stretched mode="primary" disabled={busy} onClick={() => void (isAuthenticated ? doJoin(manualCode) : joinOnLanding(manualCode))}>
          Присоединиться
        </Button>
        <Button size="l" stretched mode="secondary" style={{ marginTop: 8 }} disabled={busy} onClick={() => onResolved('dismissed')}>
          Закрыть
        </Button>
      </>
    );
  } else if (!isAuthenticated) {
    // Лендинг: приглашение принимается прямо здесь (VK ID вход в этом же окне),
    // без танцев с приложением. Код — запасной путь.
    body = (
      <>
        <Headline level="1">Вас пригласили в свадьбу</Headline>
        <Text style={{ marginTop: 10, marginBottom: 6, fontSize: 18, fontWeight: 600 }}>{name}</Text>
        <Text style={{ marginBottom: 12, color: 'var(--vkui--color_text_secondary)' }}>
          {[date, preview.owner_name ? `Организатор: ${preview.owner_name}` : null].filter(Boolean).join(' · ')}
        </Text>
        {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
        <Button size="l" stretched mode="primary" disabled={busy} onClick={() => void joinOnLanding()}>
          Присоединиться
        </Button>
        <Text style={{ marginTop: 10, marginBottom: 4, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
          Вход через ВКонтакте — свадьба сразу появится в приложении.
        </Text>
        {codeBlock}
        <Button size="l" stretched mode="secondary" disabled={busy} onClick={() => void openInVk(code)}>
          Открыть в приложении ВКонтакте
        </Button>
        <Button size="l" stretched mode="secondary" style={{ marginTop: 8 }} disabled={busy} onClick={() => onResolved('dismissed')}>
          Закрыть
        </Button>
      </>
    );
  } else if (preview.status === 'already_member' || preview.status === 'own_wedding') {
    body = (
      <>
        <Headline level="1">{preview.status === 'own_wedding' ? 'Это ваша свадьба' : 'Вы уже в этой свадьбе'}</Headline>
        <Text style={{ marginTop: 10, marginBottom: 16 }}>
          Свадьба {name}{date ? ` — ${date}` : ''}. Открыть её?
        </Text>
        {codeBlock}
        {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
        <Button size="l" stretched mode="primary" disabled={busy} onClick={() => preview.wedding_id && void doOpen(preview.wedding_id)}>
          Открыть
        </Button>
        <Button size="l" stretched mode="secondary" style={{ marginTop: 8 }} disabled={busy} onClick={() => onResolved('dismissed')}>
          Закрыть
        </Button>
      </>
    );
  } else if (preview.status === 'removed') {
    body = (
      <>
        <Headline level="1">Вы были удалены из этой свадьбы</Headline>
        <Text style={{ marginTop: 10, marginBottom: 16 }}>
          Организатор убрал вас из помощников свадьбы {name}. Новое приглашение снова откроет доступ.
        </Text>
        {codeBlock}
        {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
        <Button size="l" stretched mode="primary" disabled={busy} onClick={() => void doJoin(code)}>
          Присоединиться по приглашению
        </Button>
        <Button size="l" stretched mode="secondary" style={{ marginTop: 8 }} disabled={busy} onClick={() => onResolved('dismissed')}>
          Закрыть
        </Button>
      </>
    );
  } else {
    body = (
      <>
        <Headline level="1">Вас пригласили в свадьбу</Headline>
        <Text style={{ marginTop: 10, marginBottom: 6, fontSize: 18, fontWeight: 600 }}>{name}</Text>
        <Text style={{ marginBottom: 4, color: 'var(--vkui--color_text_secondary)' }}>
          {[date, preview.owner_name ? `Организатор: ${preview.owner_name}` : null].filter(Boolean).join(' · ')}
        </Text>
        {codeBlock}
        {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
        <Button size="l" stretched mode="primary" disabled={busy} onClick={() => void doJoin(code)}>
          Присоединиться
        </Button>
        <Button size="l" stretched mode="secondary" style={{ marginTop: 8 }} disabled={busy} onClick={() => onResolved('dismissed')}>
          Не сейчас
        </Button>
      </>
    );
  }

  return (
    <Div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <Card style={{ padding: 18 }}>{body}</Card>
      {snack && (
        <Text style={{ textAlign: 'center', marginTop: 12, color: 'var(--vkui--color_text_secondary)' }}>{snack}</Text>
      )}
    </Div>
  );
}
